import { DatePipe, registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';

import { AppComponent } from './app.component';
import { LoginComponent } from './pages/login/login.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { SharedModule } from './shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { JwtModule } from '@auth0/angular-jwt';
import { NavbarComponent } from './navbar/navbar.component';
import { UsersComponent } from './users/users.component';
import { CompaniesOverviewComponent } from './pages/companies/companies-overview/companies-overview.component';
import { EditCompanyComponent } from './pages/companies/edit/edit-company.component';
import { CompanyContactsDialogComponent } from './modals/company-contacts-dialog/company-contacts-dialog.component';
import { ProjectsComponent } from './pages/projects/overview/projects.component';
import { EditProjectComponent } from './pages/projects/edit/edit-project.component';
import {
  FullscreenOverlayContainer,
  OverlayContainer,
} from '@angular/cdk/overlay';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatExpansionModule } from '@angular/material/expansion';
import { DatePickerComponent } from './shared/placement-picker/helpers/date-picker/date-picker.component';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import {
  MatLegacyPaginatorIntl as MatPaginatorIntl,
  MatLegacyPaginatorModule as MatPaginatorModule,
} from '@angular/material/legacy-paginator';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DEFAULT_CONFIG, NgForageOptions } from 'ngforage';
import { CompanyModule } from './modules/company/company.module';
import { ProductsComponent } from './pages/products/overview/products.component';
import { ProductDetailComponent } from './pages/products/detail/product-detail.component';
import { WindowStickerSizeDetailComponent } from './pages/products/detail/window-sticker-size/window-sticker-size-detail.component';
import { SignSizeDetailComponent } from './pages/products/detail/sign-size/sign-size-detail.component';
import { SignSizeEditComponent } from './pages/products/detail/sign-size/sign-size-edit.component';
import { SignPlacementPriceDetailComponent } from './pages/products/detail/sign-placement-price/sign-placement-price-detail.component';
import { ProductPickerComponent } from './shared/product-picker/product-picker.component';
import { HelperManager } from './managers/helper.manager';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { TabsPlaceholderComponent } from './shared/tabs-placeholder/tabs-placeholder.component';
import { ProjectDescriptionComponent } from './shared/project-components/helpers/project-description/project-description.component';
import { MatPaginatorNl } from './shared/mat-paginator-nl/mat-paginator-nl';
import { InternalInvoiceComponent } from './shared/project-components/helpers/internal-invoice/internal-invoice.component';
import { MaterialPickerComponent } from './shared/project-components/helpers/material-picker/material-picker.component';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { ColorPickerComponent } from './shared/project-components/helpers/color-picker/color-picker.component';
import { ProductWrapperComponent } from './shared/project-components/product-wrapper/product-wrapper.component';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { WindowstickerDetailsDialogComponent } from './pages/projects/helpers/windowsticker-summary-picker/windowsticker-details-dialog/windowsticker-details-dialog.component';
import { SiteVisitComponent } from './shared/placement-picker/helpers/site-visit/site-visit.component';
import { EditUserComponent } from './pages/users/edit-user/edit-user.component';
import { UsersOverviewComponent } from './pages/users/users-overview/users-overview.component';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { FormsModule } from '@angular/forms';
import { NgMapsCoreModule } from '@ng-maps/core';
import { NgMapsMarkerClustererModule } from '@ng-maps/marker-clusterer';
import { NewProjectsComponent } from './pages/projects/overview/new-projects/new-projects.component';
import { ExecutedProjectsComponent } from './pages/projects/overview/executed-projects/executed-projects.component';
import { LightboxModule } from 'ngx-lightbox';
import { PmsColorListComponent } from './pages/companies/helpers/pms-color-list/pms-color-list.component';
import { ContactsOverviewComponent } from './pages/contact/contacts-overview/contacts-overview.component';
import { EditContactComponent } from './pages/contact/edit-contact/edit-contact.component';
import { InvoicingComponent } from './pages/projects/helpers/invoicing/invoicing.component';
import { AdjustmentInvoicingComponent } from './pages/projects/helpers/adjustment-invoicing/adjustment-invoicing.component';
import { MatBadgeModule } from '@angular/material/badge';
import { InvoicingProjectsComponent } from './pages/projects/overview/invoicing-projects/invoicing-projects.component';
import { SendThankYouMailComponent } from './modals/send-thank-you-mail/send-thank-you-mail.component';
import { SearchProjectsComponent } from './pages/projects/overview/search-projects/search-projects.component';
import { ProjectsTableComponent } from './pages/projects/overview/projects-table/projects-table.component';
import { DesignProjectsComponent } from './pages/projects/overview/pending-projects/design-projects.component';
import { SiteVisitProjectsComponent } from './pages/projects/overview/site-visit-projects/site-visit-projects.component';
import { QuotationProjectsComponent } from './pages/projects/overview/quotation-projects/quotation-projects.component';
import { PendingContainerComponent } from './pages/projects/overview/pending-projects/pending-container/pending-container.component';
import { PrintProjectsComponent } from './pages/projects/overview/pending-projects/print-projects.component';
import { FinishingProjectsComponent } from './pages/projects/overview/pending-projects/finishing-projects.component';
import { SupplierProjectsComponent } from './pages/projects/overview/pending-projects/supplier-projects.component';
import { VerifyLocationProjectsComponent } from './pages/projects/overview/pending-projects/verify-location-projects.component';
import { PlaceProjectsComponent } from './pages/projects/overview/pending-projects/place-projects.component';
import { QuotationSitevisitPendingComponent } from './modals/quotation-sitevisit-pending/quotation-sitevisit-pending.component';
import { PendingProjectsComponent } from './pages/projects/overview/pending-projects/pending-projects.component';
import { ConfirmModalComponent } from './components/confirm-modal/confirm-modal.component';
import { ProjectTabsContainerComponent } from './pages/projects/project-tabs-container/project-tabs-container.component';
import { EditIPAdjustmentComponent } from './components/adjustments/edit-ip-adjustment/edit-ip-adjustment.component';
import { EditViewPickerComponent } from './components/edit-view-picker/edit-view-picker.component';
import { ExEditAdjustmentComponent } from './components/ex-edit-adjustment/ex-edit-adjustment.component';
import { InEditAdjustmentComponent } from './components/in-edit-adjustment/in-edit-adjustment.component';
import { ArchiveFilterPipe } from './pipes/archive-filter';
import { OngoingProjectsComponent } from './pages/projects/overview/client/ongoing-projects/ongoing-projects.component';
import { RepoProjectsComponent } from './pages/projects/overview/client/repo-projects/repo-projects.component';
import { CompletedProjectsComponent } from './pages/projects/overview/client/completed-projects/completed-projects.component';
import { CreateViewPickerComponent } from './pages/projects/client-corner/create-view-picker/create-view-picker.component';
import { CreateViewProjectComponent } from './pages/projects/client-corner/create-view-project/create-view-project.component';
import { CreateViewAdjustmentComponent } from './pages/projects/client-corner/create-view-adjustment/create-view-adjustment.component';
import { AbstractClientProjectsComponent } from './pages/projects/overview/client/abstract-client-projects/client-projects.component';
import { AdjustmentProjectSummaryComponent } from './components/adjustment-project-summary/adjustment-project-summary.component';
import { AdjustmentProductSummaryComponent } from './components/adjustment-product-summary/adjustment-product-summary.component';
import { AdjustmentWindowstickerSummaryComponent } from './components/adjustment-product-summary/adjustment-windowsticker-summary/adjustment-windowsticker-summary.component';
import { SentInvoiceFilterPipe } from './pipes/sent-invoice-filter';
import { ArchivedProjectsComponent } from './pages/projects/overview/archived-projects/archived-projects.component';
import { GroupProjectContactPipe } from './pipes/group-project-contact';
import { ProjectContactUniqueCompanyFilter } from './pipes/project-contact-unique-company-filter';
import { EmployeeUsersOverviewComponent } from './pages/users/users-overview/employee-users-overview/employee-users-overview.component';
import { QuotationContainerComponent } from './pages/projects/overview/quotation-projects/quotation-container/quotation-container.component';
import { RejectedQuotationProjectsComponent } from './pages/projects/overview/quotation-projects/rejected-quotation-projects.component';
import { AdjustmentBoardSummaryComponent } from './components/adjustment-product-summary/adjustment-board-summary/adjustment-board-summary.component';
import { SendQuotationMailComponent } from './modals/send-quotation-mail/send-quotation-mail.component';
import { MatBadgeIconDirective } from './directives/mat-badge-icon.directive';
import { CreateOldSystemProjectComponent } from './pages/projects/create-old-system-project/create-old-system-project.component';
import { AdjustmentOtherProductSummaryComponent } from './components/adjustment-product-summary/adjustment-other-product-summary/adjustment-other-product-summary.component';
import { PendingContainerClassPipe } from './pipes/pending-container-class-pipe';
import { ApiInterceptor } from './services/api-interceptor';
import { PlacerNotesDialogComponent } from './modals/placer-notes-dialog/placer-notes-dialog.component';
import { PlacementHeaderComponent } from './pages/projects/edit/placement-header/placement-header.component';
import { ResetQuotationSentComponent } from './modals/reset-quotation-sent/reset-quotation-sent.component';
import { PlacerPlanningViewComponent } from './pages/planning/placer-planning-view/placer-planning-view.component';
import { PlacerNavbarComponent } from './placer-navbar/placer-navbar.component';
import { AtelierNavbarComponent } from './atelier-navbar/atelier-navbar.component';
import { NotificationMailComponent } from './modals/notification-mail/notification-mail.component';
import { FollowedQuotationProjectsComponent } from './pages/projects/overview/quotation-projects/followed-quotation-projects.component';
import { ProjectPlacerFormComponent } from './pages/projects/placer/project-placer-form/project-placer-form.component';
import { DelayedProjectsComponent } from './pages/projects/overview/delayed-projects/delayed-projects.component';
import { LocationVerificationDialogComponent } from './modals/location-verification-dialog/location-verification-dialog.component';
import { LocationVerificationProductDisplayComponent } from './modals/location-verification-dialog/location-verification-product-display/location-verification-product-display.component';
import { AppPipesModule } from './modules/app-pipes/app-pipes.module';
import { PlanningModule } from './modules/planning/planning.module';
import { ProjectsModule } from './modules/projects/projects.module';
import { AdjustmentInvoicingContainerComponent } from './pages/projects/helpers/adjustment-invoicing-container/adjustment-invoicing-container.component';
import { ImmoIpModule } from './pages/immo-ip/immo-ip.module';
import { UsersModule } from './modules/users/users.module';
import { ClientCornerModule } from './modules/client-corner/client-corner.module';
import { ClientCornerPendingOverviewComponent } from './pages/immo-ip/overview/client-pending/client-corner-pending-overview.component';
import { EditAdjustmentComponent } from './components/adjustments/edit-adjustment/edit-adjustment.component';
import { AtelierStockModule } from './pages/atelier/atelier-stock.module';
import { RequestAccountComponent } from './pages/request-account/request-account.component';
import { GOOGLE_MAPS_API_CONFIG, NgMapsGoogleModule } from '@ng-maps/google';
import { GOOGLE_MAPS_API_KEY } from '../globals/globals';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PrintProductionProductComponent } from './pages/products/detail/print-production/overview-table/print-production-product.component';
import { EditPrintProductionModalComponent } from './pages/products/detail/print-production/edit-print-production-modal/edit-print-production-modal.component';
import { PriceTableComponent } from './pages/products/detail/print-production/price-table-component/price-table.component';
import { ClientPortalConfigComponent } from './pages/configuration/client-portal-config/client-portal-config.component';
import { InfoPrintProductionModalComponent } from './pages/products/detail/print-production/info-print-production-modal/info-print-production-modal.component';
import { AuthRedirectComponent } from './pages/auth/auth-redirect-component/auth-redirect.component';

registerLocaleData(localeNl);

export function tokenGetter() {
  return localStorage.getItem('inpress_access_token');
}

export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    NavbarComponent,
    PlacerNavbarComponent,
    AtelierNavbarComponent,
    UsersComponent,
    CompaniesOverviewComponent,
    EditCompanyComponent,
    CompanyContactsDialogComponent,
    PmsColorListComponent,
    ProjectsComponent,
    EditProjectComponent,
    PlacementHeaderComponent,
    ProductPickerComponent,
    SiteVisitComponent,
    ProductsComponent,
    ProductDetailComponent,
    WindowStickerSizeDetailComponent,
    SignSizeDetailComponent,
    SignSizeEditComponent,
    SignPlacementPriceDetailComponent,
    ArchiveFilterPipe,
    ProjectDescriptionComponent,
    TabsPlaceholderComponent,
    InternalInvoiceComponent,
    MaterialPickerComponent,
    ColorPickerComponent,
    ProductWrapperComponent,
    DatePickerComponent,
    WindowstickerDetailsDialogComponent,
    EditUserComponent,
    UsersOverviewComponent,
    EmployeeUsersOverviewComponent,
    NewProjectsComponent,
    ExecutedProjectsComponent,
    ArchivedProjectsComponent,
    SearchProjectsComponent,
    ContactsOverviewComponent,
    EditContactComponent,
    InvoicingComponent,
    AdjustmentInvoicingComponent,
    InvoicingProjectsComponent,
    SendThankYouMailComponent,
    SendQuotationMailComponent,
    ProjectsTableComponent,
    DesignProjectsComponent,
    SiteVisitProjectsComponent,
    QuotationContainerComponent,
    QuotationProjectsComponent,
    RejectedQuotationProjectsComponent,
    PendingContainerComponent,
    PrintProjectsComponent,
    FinishingProjectsComponent,
    SupplierProjectsComponent,
    VerifyLocationProjectsComponent,
    PlaceProjectsComponent,
    QuotationSitevisitPendingComponent,
    PendingProjectsComponent,
    ConfirmModalComponent,
    ProjectTabsContainerComponent,
    EditIPAdjustmentComponent,
    EditAdjustmentComponent,
    EditViewPickerComponent,
    ExEditAdjustmentComponent,
    InEditAdjustmentComponent,
    AbstractClientProjectsComponent,
    OngoingProjectsComponent,
    RepoProjectsComponent,
    CompletedProjectsComponent,
    CreateViewProjectComponent,
    CreateViewPickerComponent,
    CreateViewAdjustmentComponent,
    AdjustmentProjectSummaryComponent,
    AdjustmentProductSummaryComponent,
    AdjustmentWindowstickerSummaryComponent,
    AdjustmentBoardSummaryComponent,
    SentInvoiceFilterPipe,
    GroupProjectContactPipe,
    ProjectContactUniqueCompanyFilter,
    MatBadgeIconDirective,
    AdjustmentOtherProductSummaryComponent,
    CreateOldSystemProjectComponent,
    PendingContainerClassPipe,
    PlacerNotesDialogComponent,
    NotificationMailComponent,
    PlacerPlanningViewComponent,
    PlacerNotesDialogComponent,
    ResetQuotationSentComponent,
    FollowedQuotationProjectsComponent,
    ProjectPlacerFormComponent,
    DelayedProjectsComponent,
    LocationVerificationDialogComponent,
    LocationVerificationProductDisplayComponent,
    AdjustmentInvoicingContainerComponent,
    ClientCornerPendingOverviewComponent,
    EditAdjustmentComponent,
    RequestAccountComponent,
    PrintProductionProductComponent,
    EditPrintProductionModalComponent,
    PriceTableComponent,
    ClientPortalConfigComponent,
    InfoPrintProductionModalComponent,
    AuthRedirectComponent,
  ],
  imports: [
    BrowserModule,
    LightboxModule,
    SharedModule,
    BrowserAnimationsModule,
    HttpClientModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
        disallowedRoutes: ['localhost:8080/api/auth/'],
      },
    }),
    NgMapsCoreModule,
    NgMapsGoogleModule,
    NgMapsMarkerClustererModule,
    FormsModule,
    MatButtonModule,
    MatStepperModule,
    MatCardModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatExpansionModule,
    MatDividerModule,
    NgxMaskModule.forRoot(options),
    MatRadioModule,
    MatPaginatorModule,
    MatDatepickerModule,
    MatMenuModule,
    MatMomentDateModule,
    MatDialogModule,
    MatListModule,
    MatSlideToggleModule,
    MatBadgeModule,
    CompanyModule,
    AppPipesModule,
    PlanningModule,
    ProjectsModule,
    ImmoIpModule,
    UsersModule,
    ClientCornerModule,
    AtelierStockModule,
    TooltipModule,
  ],
  providers: [
    DatePipe,
    HelperManager,
    { provide: MatPaginatorIntl, useClass: MatPaginatorNl },
    { provide: MAT_DATE_LOCALE, useValue: 'nl-BE' },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    {
      provide: DEFAULT_CONFIG,
      useValue: { name: 'InpressStorage' } as NgForageOptions,
    },
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    {
      provide: GOOGLE_MAPS_API_CONFIG,
      useValue: {
        // please get your own API key here:
        // https://developers.google.com/maps/documentation/javascript/get-api-key?hl=en
        apiKey: GOOGLE_MAPS_API_KEY,
      },
    },
  ],
  exports: [],
  bootstrap: [AppComponent],
})
export class AppModule {}
